// AdminGate.js — /admin/gate
// Phase 3 admin slice for the smart gate: config status, manual open,
// per-user provisioning (provisionGateAccess), and the gateAccess audit log.
// Doubles as the Phase 1 test harness while dryRun is on.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Alert, IconButton, Tooltip, Switch, FormControlLabel,
  Tabs, Tab, Menu, MenuItem,
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyIcon from '@mui/icons-material/Key';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, orderBy, limit, setDoc, deleteField } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase';
import Layout from '../components/Layout';
import GateCardsPanel from '../components/GateCardsPanel';
import KeypadHistory from '../components/KeypadHistory';

const functions = getFunctions();

const NAVY = '#0D1E35';
const GOLD = '#C9A84C';
// Mirrors PIN_LIMIT_EXEMPT_DEFAULT in functions/index.js, which decides. Codes
// that are shared by design — a household at drop-off, a renting group — plus
// staff, who are in and out all day.
const PIN_HOLDER_TYPES = ['parent', 'coach', 'renter', 'event', 'member', 'other'];
const LIMIT_EXEMPT_DEFAULT = ['parent', 'renter', 'event', 'coach', 'master'];

const RESULT_COLORS = { granted: '#27AE60', denied: '#C0392B', error: '#E67E22' };

// What a log row's result chip says. A keypad entry names what opened the gate;
// the rest of the log (enrollments, reveals, admin opens) stays as recorded. A
// row knows only that it was a card, so whether it was a fob comes from the card
// record it points at.
const resultLabel = (l, cardKinds) => {
  const keypadEntry = l.source === 'keypad' && (!l.type || l.type === 'offline_entry');
  if (l.result !== 'granted' || !keypadEntry) return l.result;
  if (l.credential !== 'card') return 'PIN accepted';
  return cardKinds[l.pinId] === 'fob' ? 'Key fob accepted' : 'Card accepted';
};

// Gate health states, as written by pollGateStatus. 'unknown' is amber, not
// red: it means the check could not get an answer, which is a different
// problem from a relay that answered and said it was down.
const STATE_COLORS = {
  online: '#27AE60', offline: '#C0392B', unknown: '#E67E22',
  error: '#E67E22', dry_run: '#7F8C8D', not_configured: '#7F8C8D',
};
const STATE_LABELS = {
  online: 'Online', offline: 'OFFLINE', unknown: 'Unknown',
  error: 'Check failed', dry_run: 'Dry run', not_configured: 'Not configured',
};

// Durations read as "4h 12m", not 15120000. Only ever used on a span that
// already happened, so no future/negative case to handle.
const fmtDur = (ms) => {
  if (!ms || ms < 0) return '—';
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const fmtTs = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

function AdminGate() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [ready, setReady] = useState(false);
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [cardKinds, setCardKinds] = useState({});
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState('');           // uid being provisioned / 'manual' / 'config'
  const [msg, setMsg] = useState(null);           // { severity, text }
  const [openDialog, setOpenDialog] = useState(false);
  const [openReason, setOpenReason] = useState('');
  const [migBusy, setMigBusy] = useState(false);
  const [migReport, setMigReport] = useState(null);
  const [backfillReport, setBackfillReport] = useState(null);
  const [chargeReport, setChargeReport] = useState(null);
  const [refundReport, setRefundReport] = useState(null);
  const [hwForm, setHwForm] = useState({ server: '', sbcDeviceId: '', holdOpenDeviceId: '', uniDeviceId: '' });
  // Entry limits. Blank means "use the built-in default"; 0 means off. Kept as
  // strings so a field can be emptied without becoming 0, which would read as
  // "disabled" rather than "not set".
  const [limForm, setLimForm] = useState({ cooldown: '', maxHour: '', failThreshold: '', lockMinutes: '', exempt: [], enforce: false });
  const [limDirty, setLimDirty] = useState(false);
  const [limSaving, setLimSaving] = useState(false);
  const [hwSaving, setHwSaving] = useState(false);
  const [hwDirty, setHwDirty] = useState(false);
  const [holdMenuEl, setHoldMenuEl] = useState(null);
  const [holdBusy, setHoldBusy] = useState(false);
  const [migConfirm, setMigConfirm] = useState(false);
  // Member shells: paying members with no Torch account.
  const [shellBusy, setShellBusy] = useState('');
  const [shellReport, setShellReport] = useState(null);
  const [shells, setShells] = useState([]);
  const [shellLinkEmail, setShellLinkEmail] = useState({});
  // Is the relay reachable? null while unknown — see getGateStatus.
  const [gateStatus, setGateStatus] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);

  // Which pane is showing. The page had grown to seven stacked sections and
  // the log — the thing you open it for when something is wrong — was last.
  const [tab, setTab] = useState('status');
  // Recorded health history, written by the pollGateStatus scheduler. Distinct
  // from gateStatus above, which is a live one-off check of right now.
  const [statusLog, setStatusLog] = useState([]);
  // Keypad PINs
  const [pins, setPins] = useState([]);
  const [pinsLoaded, setPinsLoaded] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [pinBusy, setPinBusy] = useState('');        // pin id, 'issue', 'bulk:parents', ...
  // Checking a code without opening anything. Before this, the only way to
  // find out whether a PIN worked was to stand at the gate and type it — and
  // when nothing happened, nothing could say whether the code or the keypad
  // was at fault.
  const [testPin, setTestPin] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState(null);  // see openIssueDialog
  const [pinReveal, setPinReveal] = useState(null);  // { pin, label, emailed }
  const [bulkReport, setBulkReport] = useState(null);
  // The one-off code. Held in the clear while the page is open because reading
  // it out is the entire job — hiding it behind a reveal click would just add a
  // step to the thing an admin came here to do.
  const [masterPin, setMasterPin] = useState(null);   // { pin, pinId, issuedAt, minted }
  const [masterBusy, setMasterBusy] = useState(false);
  const [masterCopied, setMasterCopied] = useState(false);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) { navigate('/'); return; }
      const snap = await getDoc(doc(db, 'users', u.uid));
      const roles = snap.exists() ? (snap.data().roles || []) : [];
      if (!roles.includes('admin')) { navigate('/dashboard'); return; }
      setIsAdmin(true);
      // loadMasterPin is deliberately NOT here — see the card below. Reading
      // the master PIN writes an audit row, so loading it with the page made
      // 32 of the log's 55 entries say a PIN was revealed when all anyone did
      // was open this screen.
      await Promise.all([loadConfig(), loadUsers(), loadLogs(), loadPins(), loadShells(), loadStatusLog()]);
      loadGateStatus();   // not awaited: a slow cloud must not hold up the page
      setReady(true);
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = async () => {
    const snap = await getDoc(doc(db, 'settings', 'gateConfig'));
    const cfg = snap.exists() ? snap.data() : null;
    setConfig(cfg);
    // Don't stomp on a half-typed edit when the config reloads after a save.
    setHwForm(prev => (hwDirty ? prev : {
      server: cfg?.shelly?.server || '',
      sbcDeviceId: cfg?.shelly?.sbcDeviceId || '',
      holdOpenDeviceId: cfg?.shelly?.holdOpenDeviceId || cfg?.shelly?.resetDeviceId || '',
      uniDeviceId: cfg?.shelly?.uniDeviceId || '',
    }));
    const rl = cfg?.rateLimit || {};
    const lo = cfg?.keypad?.lockout || {};
    const num = (v) => (v === 0 || v ? String(v) : '');
    setLimForm({
      cooldown: num(rl.perUserCooldownSeconds),
      maxHour: num(rl.globalMaxPerHour),
      failThreshold: num(lo.failThreshold),
      lockMinutes: num(lo.baseLockMinutes),
      exempt: Array.isArray(rl.exemptHolderTypes) ? rl.exemptHolderTypes : LIMIT_EXEMPT_DEFAULT,
      enforce: rl.enforce === true,
    });
    setLimDirty(false);
  };

  const saveLimits = async () => {
    setLimSaving(true); setMsg(null);
    try {
      // A blank field is removed rather than written as 0 — the two mean
      // different things here, and writing 0 for "I left it empty" would switch
      // a limit off by accident.
      const n = (v) => {
        const t = String(v).trim();
        if (t === '') return null;
        const x = Number(t);
        if (!Number.isFinite(x) || x < 0) throw new Error(`"${t}" is not a number of zero or more.`);
        return Math.round(x);
      };
      const del = deleteField();
      const set = (v) => (v === null ? del : v);
      await setDoc(doc(db, 'settings', 'gateConfig'), {
        rateLimit: {
          perUserCooldownSeconds: set(n(limForm.cooldown)),
          globalMaxPerHour: set(n(limForm.maxHour)),
          exemptHolderTypes: limForm.exempt,
          enforce: !!limForm.enforce,
        },
        keypad: {
          lockout: {
            failThreshold: set(n(limForm.failThreshold)),
            baseLockMinutes: set(n(limForm.lockMinutes)),
          },
        },
      }, { merge: true });
      setLimDirty(false);
      await loadConfig();
      setMsg({ severity: 'success', text: 'Entry limits saved.' });
    } catch (e) {
      setMsg({ severity: 'error', text: e.message || 'Could not save the limits.' });
    } finally { setLimSaving(false); }
  };

  const saveHardware = async () => {
    setHwSaving(true); setMsg(null);
    try {
      // shellyClient builds `https://${server}/...`, so a pasted URL would
      // produce https://https://… — strip the scheme and any trailing slash.
      const server = hwForm.server.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
      const clean = (v) => v.trim().toLowerCase().replace(/^shelly[a-z0-9]*-/i, '');
      const sbcDeviceId = clean(hwForm.sbcDeviceId);
      const holdOpenDeviceId = clean(hwForm.holdOpenDeviceId);
      const uniDeviceId = clean(hwForm.uniDeviceId);
      for (const [label, id] of [['gate', sbcDeviceId], ['hold-open', holdOpenDeviceId], ['gate sensor Uni', uniDeviceId]]) {
        if (id && !/^[0-9a-f]{12}$/.test(id)) {
          throw new Error(`The ${label} device ID should be 12 hex characters (a MAC without colons) — got "${id}".`);
        }
      }
      // An emptied field has to clear the stored id, not merge over it, or the
      // status row would keep asking about a device that is no longer there.
      await setDoc(doc(db, 'settings', 'gateConfig'),
        { shelly: { server, sbcDeviceId,
          ...(holdOpenDeviceId ? { holdOpenDeviceId } : {}),
          uniDeviceId: uniDeviceId || null } }, { merge: true });
      setHwDirty(false);
      await loadConfig();
      loadGateStatus();   // new IDs are worth proving immediately
      setMsg({ severity: 'success', text: 'Shelly hardware settings saved.' });
    } catch (e) {
      setMsg({ severity: 'error', text: e.message || 'Could not save hardware settings.' });
    } finally { setHwSaving(false); }
  };

  // minutes is the hold length when starting; omitted when releasing.
  const holdOpenAction = async (start, minutes) => {
    setHoldMenuEl(null);
    setHoldBusy(true); setMsg(null);
    try {
      const fn = httpsCallable(functions, start ? 'setGateHoldOpen' : 'releaseGateHoldOpen');
      const r = await fn(start ? { minutes: Number(minutes) } : {});
      await Promise.all([loadConfig(), loadLogs()]);
      setMsg({ severity: 'success', text: start
        ? `Gate held open until ${new Date(r.data.until).toLocaleTimeString()}.`
        : 'Gate hold-open released.' });
    } catch (e) {
      setMsg({ severity: 'error', text: e.message || 'Hold-open action failed.' });
    } finally { setHoldBusy(false); }
  };

  const loadUsers = async () => {
    const snap = await getDocs(collection(db, 'users'));
    setUsers(snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(u => !u.id.startsWith('pending_') && !u.mergedIntoUid && (u.email || u.firstName)));
  };

  const loadLogs = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'gateAccess'), orderBy('timestamp', 'desc'), limit(50)));
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error('audit log load failed', e); }
    // Revoked cards too, since their old entries are still in the log.
    try {
      const { data } = await httpsCallable(functions, 'listGateCards')({ includeRevoked: true });
      setCardKinds(Object.fromEntries((data?.cards || []).map(c => [c.id, c.kind])));
    } catch (e) { console.error('card kinds load failed', e); }
  };

  // The recorded history — a plain Firestore read, unlike loadGateStatus below,
  // which goes out to Shelly. One row per state change and nothing in between,
  // so 50 of them cover a long stretch of calendar time.
  const loadStatusLog = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'gateStatus'), orderBy('at', 'desc'), limit(50)));
      setStatusLog(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error('gate status history load failed', e); }
  };

  // Asked on load and on demand, never on a timer: it is a live call out to
  // Shelly's cloud, and an admin page left open should not hammer it.
  const loadGateStatus = async () => {
    setStatusBusy(true);
    try {
      const fn = httpsCallable(functions, 'getGateStatus');
      const r = await fn({});
      setGateStatus(r.data || null);
    } catch (e) {
      setGateStatus({ error: e.message });
    } finally { setStatusBusy(false); }
  };

  const displayName = (u) =>
    `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.displayName || u.email || u.id;

  // ── Keypad PINs ──────────────────────────────────────────────────────────
  // Codes never arrive with the list: listGatePins strips the sealed copy, and
  // reading one back is a separate call that writes an audit entry.
  const loadPins = async (withRevoked = showRevoked) => {
    try {
      const fn = httpsCallable(functions, 'listGatePins');
      const r = await fn({ includeRevoked: withRevoked });
      setPins(r.data?.pins || []);
      setPinsLoaded(true);
    } catch (e) {
      setMsg({ severity: 'error', text: `Could not load PINs: ${e.message}` });
    }
  };

  // Asks for the current master and mints one only if the club has none — see
  // ensureMasterGatePin. Called when somebody asks to see the code, never on
  // page load: the server logs a reveal every time this runs, and a log that
  // records opening a screen cannot show who actually read the PIN out.
  const loadMasterPin = async () => {
    setMasterBusy(true);
    try {
      const fn = httpsCallable(functions, 'ensureMasterGatePin');
      const r = await fn({});
      setMasterPin(r.data);
    } catch (e) {
      setMsg({ severity: 'error', text: `Could not read the master PIN: ${e.message}` });
    } finally { setMasterBusy(false); }
  };

  const rotateMaster = async () => {
    if (!window.confirm('Replace the master PIN?\n\nThe current code stops working immediately — '
      + 'anyone you have already given it to will be refused.')) return;
    setMasterBusy(true); setMsg(null);
    try {
      const fn = httpsCallable(functions, 'rotateMasterGatePin');
      const r = await fn({ reason: 'replaced from the admin page' });
      setMasterPin({ pin: r.data.pin, pinId: r.data.pinId, issuedAt: Date.now(), minted: true });
      setMasterCopied(false);
      setMsg({ severity: 'success', text: 'New master PIN issued. The old one no longer opens the gate.' });
      await loadPins();
    } catch (e) {
      setMsg({ severity: 'error', text: `Could not replace the master PIN: ${e.message}` });
    } finally { setMasterBusy(false); }
  };

  const copyMaster = async () => {
    try {
      await navigator.clipboard.writeText(masterPin.pin);
      setMasterCopied(true);
      setTimeout(() => setMasterCopied(false), 2000);
    } catch (e) {
      setMsg({ severity: 'warning', text: 'Could not reach the clipboard — read it off the screen.' });
    }
  };

  const emptyIssueForm = {
    holderType: 'parent', holderId: '', holderName: '', label: '',
    userSearch: '', days: [], hoursStart: '', hoursEnd: '', expiresAt: '', notes: '', email: true,
  };
  const openIssueDialog = () => { setIssueForm(emptyIssueForm); setIssueOpen(true); };

  const rulesFromForm = (f) => {
    const rules = {};
    if (f.days.length) rules.allowedDaysOfWeek = f.days;
    if (f.hoursStart && f.hoursEnd) { rules.allowedHoursStart = f.hoursStart; rules.allowedHoursEnd = f.hoursEnd; }
    if (f.expiresAt) rules.expiresAt = new Date(`${f.expiresAt}T23:59:59`).toISOString();
    return rules;
  };

  const checkPin = async () => {
    setPinBusy('test'); setMsg(null); setTestResult(null);
    try {
      const fn = httpsCallable(functions, 'testGatePin');
      const { data } = await fn({ pin: testPin });
      setTestResult(data);
      // Not kept on screen: the point of checking a code is not to leave it
      // sitting in a text box on an unattended laptop.
      setTestPin('');
    } catch (e) {
      setMsg({ severity: 'error', text: `Could not check that PIN: ${e.message}` });
    }
    setPinBusy('');
  };

  const issuePin = async () => {
    const f = issueForm;
    if (!f.holderId && !f.label.trim()) {
      setMsg({ severity: 'warning', text: 'Pick an account, or give the PIN a label so the log can name it.' });
      return;
    }
    setPinBusy('issue'); setMsg(null);
    try {
      const fn = httpsCallable(functions, 'issueGatePin');
      const r = await fn({
        holderType: f.holderType,
        holderId: f.holderId || null,
        label: f.label.trim() || f.holderName,
        rules: rulesFromForm(f),
        notes: f.notes.trim(),
        email: f.email,
      });
      setIssueOpen(false);
      setPinReveal({
        pin: r.data.pin, label: f.label.trim() || f.holderName,
        emailed: r.data.emailed, sentTo: r.data.sentTo,
        emailNote: r.data.emailError ? `The email failed: ${r.data.emailError}` : r.data.emailNote,
      });
      await loadPins();
    } catch (e) {
      setMsg({ severity: 'error', text: `Could not issue that PIN: ${e.message}` });
    } finally { setPinBusy(''); }
  };

  const revealPin = async (p) => {
    setPinBusy(p.id); setMsg(null);
    try {
      const fn = httpsCallable(functions, 'revealGatePin');
      const r = await fn({ pinId: p.id });
      setPinReveal({ pin: r.data.pin, label: p.label, emailed: null });
    } catch (e) {
      setMsg({ severity: 'error', text: `Could not read that PIN: ${e.message}` });
    } finally { setPinBusy(''); }
  };

  const rotatePin = async (p) => {
    setPinBusy(p.id); setMsg(null);
    try {
      const fn = httpsCallable(functions, 'rotateGatePin');
      const r = await fn({ pinId: p.id, graceHours: 12 });
      setPinReveal({ pin: r.data.pin, label: p.label, emailed: r.data.emailed });
      setMsg({ severity: 'info', text: 'New PIN issued. The old one keeps working for 12 hours so nobody is locked out mid-week.' });
      await loadPins();
    } catch (e) {
      setMsg({ severity: 'error', text: `Could not rotate that PIN: ${e.message}` });
    } finally { setPinBusy(''); }
  };

  const revokePin = async (p) => {
    setPinBusy(p.id); setMsg(null);
    try {
      const fn = httpsCallable(functions, 'revokeGatePin');
      await fn({ pinId: p.id, reason: 'Revoked from Smart Gate admin' });
      setMsg({ severity: 'success', text: `PIN for ${p.label} revoked.` });
      await loadPins();
    } catch (e) {
      setMsg({ severity: 'error', text: `Could not revoke that PIN: ${e.message}` });
    } finally { setPinBusy(''); }
  };

  // Dry run first, always: it reports who would get one without issuing or
  // mailing anything.
  const bulkIssue = async (audience, dryRun) => {
    setPinBusy(`bulk:${audience}`); setMsg(null); setBulkReport(null);
    try {
      const fn = httpsCallable(functions, 'bulkIssueGatePins');
      const r = await fn({ audience, dryRun });
      setBulkReport(r.data);
      if (!dryRun) await loadPins();
    } catch (e) {
      setMsg({ severity: 'error', text: `Bulk issue failed: ${e.message}` });
    } finally { setPinBusy(''); }
  };

  const pinWindow = (rules) => {
    const r = rules || {};
    const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const bits = [];
    bits.push(r.allowedDaysOfWeek?.length ? r.allowedDaysOfWeek.map(d => DAYS[d]).join(' ') : 'Any day');
    if (r.allowedHoursStart && r.allowedHoursEnd) bits.push(`${r.allowedHoursStart}–${r.allowedHoursEnd}`);
    if (r.expiresAt) bits.push(`until ${fmtTs(r.expiresAt)}`);
    return bits.join(' · ');
  };

  const provision = async (userId, enabled, regenerate = false) => {
    setBusy(userId); setMsg(null);
    try {
      const fn = httpsCallable(functions, 'provisionGateAccess');
      await fn({ userId, enabled, regenerate });
      setMsg({ severity: 'success', text: `Gate access ${enabled ? (regenerate ? 'secret regenerated' : 'enabled') : 'disabled'}.` });
      await loadUsers();
    } catch (err) {
      setMsg({ severity: 'error', text: `Provisioning failed: ${err.message}` });
    } finally { setBusy(''); }
  };

  // Runs the member entry path for a chosen user — same code as a QR scan,
  // minus the TOTP check. The only way to exercise day-pass consumption until
  // a member-facing entry flow exists.
  const testMemberEntry = async (userId) => {
    setBusy(userId); setMsg(null);
    try {
      const fn = httpsCallable(functions, 'adminTestMemberEntry');
      const r = await fn({ userId });
      const d = r.data || {};
      setMsg({
        severity: d.ok ? 'success' : 'warning',
        text: d.ok
          ? `Entry granted for ${d.userName || userId}.${d.dayPassConsumed ? ' Day pass consumed — expiry moved to today\u2019s close.' : ''}`
          : `Entry denied: ${d.reason}`,
      });
      await Promise.all([loadUsers(), loadLogs()]);
    } catch (err) {
      setMsg({ severity: 'error', text: `Test entry failed: ${err.message}` });
    } finally { setBusy(''); }
  };

  const manualOpen = async () => {
    setBusy('manual'); setMsg(null);
    try {
      const fn = httpsCallable(functions, 'triggerGateManual');
      await fn({ reason: openReason.trim() });
      setMsg({ severity: 'success', text: config?.dryRun ? 'Dry run: gate trigger logged, no hardware called.' : 'Gate triggered.' });
      setOpenDialog(false); setOpenReason('');
      await loadLogs();
    } catch (err) {
      setMsg({ severity: 'error', text: `Gate trigger failed: ${err.message}` });
    } finally { setBusy(''); }
  };

  const loadShells = async () => {
    const snap = await getDocs(collection(db, 'users'));
    setShells(snap.docs
      .filter(d => d.data().memberShell === true && d.data().registrationLevel === 'mini')
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)));
  };

  const runShells = async (dryRun) => {
    setShellBusy(dryRun ? 'dry' : 'live'); setMsg(null);
    try {
      const r = await httpsCallable(functions, 'createStripeMemberShells', { timeout: 300000 })({ dryRun });
      setShellReport(r.data);
      setMsg({ severity: dryRun ? 'info' : 'success',
        text: `${dryRun ? 'DRY RUN' : 'LIVE RUN'}: ${r.data.created.length} member account${r.data.created.length === 1 ? '' : 's'} ${dryRun ? 'would be' : ''} created, ${r.data.skipped.length} skipped.` });
      if (!dryRun) await loadShells();
    } catch (err) {
      setMsg({ severity: 'error', text: `Create member accounts failed: ${err.message}` });
    } finally { setShellBusy(''); }
  };

  const linkShell = async (shell) => {
    const userEmail = (shellLinkEmail[shell.id] || '').trim();
    if (!userEmail) return;
    setShellBusy(shell.id); setMsg(null);
    try {
      const r = await httpsCallable(functions, 'adminLinkMiniAccount')({ miniId: shell.id, userEmail });
      setMsg({ severity: 'success',
        text: `Linked ${shell.firstName} ${shell.lastName} to ${r.data.name || userEmail}: ${r.data.purchases} purchase${r.data.purchases === 1 ? '' : 's'} moved${r.data.restamped ? ', Stripe subscription re-pointed' : ''}.` });
      setShellLinkEmail(prev => ({ ...prev, [shell.id]: '' }));
      await loadShells();
    } catch (err) {
      setMsg({ severity: 'error', text: `Link failed: ${err.message}` });
    } finally { setShellBusy(''); }
  };

  const runMigration = async (dryRun) => {
    setMigBusy(true); setMsg(null);
    if (!dryRun) setMigConfirm(false);
    try {
      const fn = httpsCallable(functions, 'migrateStripeMembers');
      const r = await fn({ dryRun });
      setMigReport(r.data);
      setMsg({ severity: dryRun ? 'info' : 'success',
        text: `${dryRun ? 'DRY RUN' : 'LIVE RUN'} complete: ${r.data.migrated.length} migrated, ${r.data.alreadyLinked.length} already linked, ${r.data.unmatchedEmail.length} unmatched email, ${r.data.unmatchedTier.length} unmatched tier, ${r.data.conflicts.length} conflicts.` });
    } catch (err) {
      setMsg({ severity: 'error', text: `Migration failed: ${err.message}` });
    } finally { setMigBusy(false); }
  };

  const runBackfill = async (dryRun) => {
    if (!dryRun && !window.confirm('LIVE backfill: writes a purchases record for every paid membership invoice, including subscriptions with no Torch account yet (recorded with userId null). Continue?')) return;
    setMigBusy(true); setMsg(null); setBackfillReport(null);
    try {
      // Walks full Stripe subscription history — allow the server's 540s, not the 70s default
      const fn = httpsCallable(functions, 'backfillMembershipPurchases', { timeout: 540000 });
      const r = await fn({ dryRun });
      const d = r.data;
      setBackfillReport(d);
      setMsg({ severity: dryRun ? 'info' : 'success',
        text: `Purchase backfill ${dryRun ? 'DRY RUN' : 'LIVE'}: ${d.subs} subscriptions, ${d.written} records ($${d.totalDollars}), `
          + `${d.skippedZero} zero-amount skipped, ${d.skippedStartCovered} covered by native start records.`
          + (d.unlinkedSubs ? ` Includes ${d.unlinkedSubs} unlinked subscription${d.unlinkedSubs === 1 ? '' : 's'} — ${d.unlinkedWritten} records ($${d.unlinkedDollars}).` : '') });
    } catch (err) {
      setMsg({ severity: 'error', text: `Backfill failed: ${err.message}` });
    } finally { setMigBusy(false); }
  };

  const runChargeBackfill = async (dryRun) => {
    if (!dryRun && !window.confirm('LIVE: writes a purchases record for every succeeded Stripe charge that is not already recorded and can be classified. Continue?')) return;
    setMigBusy(true); setMsg(null); setChargeReport(null);
    try {
      const fn = httpsCallable(functions, 'backfillStandaloneCharges', { timeout: 540000 });
      const r = await fn({ dryRun });
      const d = r.data;
      setChargeReport(d);
      setMsg({ severity: dryRun ? 'info' : 'success',
        text: `Charge backfill ${dryRun ? 'DRY RUN' : 'LIVE'}: ${d.chargesSeen} charges seen, `
          + `${d.coveredByInvoice + d.coveredBySession + d.coveredById} already recorded, `
          + `${d.written} to write ($${d.totalDollars}), ${d.unclassified?.length || 0} unclassified ($${d.unclassifiedDollars}).` });
    } catch (err) {
      setMsg({ severity: 'error', text: `Charge backfill failed: ${err.message}` });
    } finally { setMigBusy(false); }
  };

  const runRefundBackfill = async (dryRun) => {
    if (!dryRun && !window.confirm('LIVE: marks every purchase whose payment was refunded in Stripe as refunded or partially refunded. Continue?')) return;
    setMigBusy(true); setMsg(null); setRefundReport(null);
    try {
      const fn = httpsCallable(functions, 'backfillMembershipRefunds', { timeout: 540000 });
      const d = (await fn({ dryRun })).data;
      setRefundReport(d);
      setMsg({ severity: dryRun ? 'info' : 'success',
        text: `Refund backfill ${dryRun ? 'DRY RUN' : 'LIVE'}: ${d.charges} refunded charges, `
          + `${d.written} purchase${d.written === 1 ? '' : 's'} to mark ($${d.refundedDollars}), `
          + `${d.alreadyRecorded} already recorded, ${d.leagueRegistrations} league, ${d.unmatched.length} unmatched.` });
    } catch (err) {
      setMsg({ severity: 'error', text: `Refund backfill failed: ${err.message}` });
    } finally { setMigBusy(false); }
  };

  const toggleConfigFlag = async (field, value) => {
    setBusy('config');
    try {
      await setDoc(doc(db, 'settings', 'gateConfig'), { [field]: value }, { merge: true });
      await loadConfig();
      // Arming the hardware is exactly when you want to know it answers.
      if (field === 'dryRun') loadGateStatus();
    } finally { setBusy(''); }
  };

  // Same thing one level down, for the shelly sub-object. Re-reads the status
  // straight away: flipping the reed polarity is something you do while
  // standing at an open gate watching the chip, and a stale chip would look
  // like the flip did nothing.
  const toggleShellyFlag = async (field, value) => {
    setBusy('config');
    try {
      await setDoc(doc(db, 'settings', 'gateConfig'), { shelly: { [field]: value } }, { merge: true });
      await loadConfig();
      loadGateStatus();
    } finally { setBusy(''); }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const withAccess = users.filter(u => u.gateAccess);
    const pool = q
      ? users.filter(u => displayName(u).toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      : withAccess;
    return pool
      .sort((a, b) => displayName(a).localeCompare(displayName(b)))
      .slice(0, 25);
  }, [users, search]);

  if (!ready) {
    return (
      <Layout>
        <Box sx={{ minHeight: '100vh', backgroundColor: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: GOLD }} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
    <Box sx={{ minHeight: '100vh', backgroundColor: NAVY, p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2rem', letterSpacing: '3px', color: 'white' }}>
            Smart Gate
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Hidden while a hold is running — the loud banner below carries
                the state and the way out. */}
            {!config?.holdOpen?.active && (<>
              <Button variant="outlined" disabled={holdBusy || !hwForm.holdOpenDeviceId}
                endIcon={<ArrowDropDownIcon />}
                onClick={e => setHoldMenuEl(e.currentTarget)}
                sx={{ color: '#E67E22', borderColor: '#E67E22', fontWeight: 700 }}>
                {holdBusy ? 'Opening…' : 'Hold Open'}
              </Button>
              <Menu anchorEl={holdMenuEl} open={Boolean(holdMenuEl)} onClose={() => setHoldMenuEl(null)}>
                {[30, 60, 120, 180, 240, 360, 480].map(m => (
                  <MenuItem key={m} onClick={() => holdOpenAction(true, m)}>
                    {m < 60 ? `${m} minutes` : `${m / 60} hour${m === 60 ? '' : 's'}`}
                  </MenuItem>
                ))}
              </Menu>
            </>)}
            <Button
              variant="contained" startIcon={<LockOpenIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{ backgroundColor: GOLD, color: NAVY, fontWeight: 700, '&:hover': { backgroundColor: '#b8963f' } }}
            >
              Open Gate
            </Button>
          </Box>
        </Box>

        {/* Hold-open banner — deliberately loud. "Is the gate still open?"
            should never require driving over to find out. */}
        {config?.holdOpen?.active && (
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(230,126,34,0.18)', border: '2px solid #E67E22' }}>
            <Typography sx={{ color: '#F0B27A', fontWeight: 800, fontSize: '1.05rem' }}>
              GATE IS HELD OPEN
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Auto-releases at {fmtTs(config.holdOpen.until)}
              {config.holdOpen.byName ? ` · started by ${config.holdOpen.byName}` : ''}
              {config.holdOpen.reason ? ` · ${config.holdOpen.reason}` : ''}
            </Typography>
            <Button size="small" variant="contained" disabled={holdBusy}
              onClick={() => holdOpenAction(false)}
              sx={{ mt: 1, backgroundColor: '#C0392B', fontWeight: 700 }}>
              {holdBusy ? 'Closing…' : 'Close gate now'}
            </Button>
          </Paper>
        )}


        {/* Seven stacked sections had made this a scrolling page, with the log
            — the thing you open it for when something is wrong — at the bottom.
            Status leads because it answers the question that brings you here. */}
        <Tabs
          value={tab} onChange={(e, v) => setTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{
            mb: 2, borderBottom: '1px solid rgba(201,168,76,0.25)',
            '& .MuiTab-root': { color: 'rgba(255,255,255,0.6)', fontWeight: 700, textTransform: 'none' },
            '& .Mui-selected': { color: GOLD },
            '& .MuiTabs-indicator': { backgroundColor: GOLD },
          }}
        >
          <Tab value="status" label="Status" />
          <Tab value="hardware" label="Hardware" />
          <Tab value="pins" label="PINs" />
          <Tab value="access" label="Access" />
          <Tab value="migration" label="Migration" />
          <Tab value="log" label="Log" />
        </Tabs>

        {/* Outside the panes: an action on any tab can raise one. */}
        {msg && <Alert severity={msg.severity} onClose={() => setMsg(null)} sx={{ mb: 2 }}>{msg.text}</Alert>}

        {tab === 'status' && (<>
        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
          {config ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip size="small" label={config.enabled !== false ? 'Gate Enabled' : 'Gate Disabled'}
                sx={{ backgroundColor: config.enabled !== false ? '#27AE60' : '#C0392B', color: 'white', fontWeight: 700 }} />
              <Chip size="small" label={config.dryRun ? 'DRY RUN — no hardware calls' : 'LIVE — hardware armed'}
                sx={{ backgroundColor: config.dryRun ? '#E67E22' : '#27AE60', color: 'white', fontWeight: 700 }} />
              {(() => {
                const s = gateStatus;
                const chip = (label, bg, title, key) => (
                  <Tooltip title={title} key={key}>
                    <Chip size="small" label={label} onClick={loadGateStatus}
                      sx={{ backgroundColor: bg, color: 'white', fontWeight: 700, cursor: 'pointer' }} />
                  </Tooltip>
                );
                if (statusBusy && !s) return chip('Checking gate…', '#7F8C8D', 'Asking Shelly whether the relays are reachable');
                if (!s) return chip('Gate status —', '#7F8C8D', 'Click to check whether the relays are reachable');
                if (s.error) return chip('Status check failed', '#E67E22', s.error);
                if (s.dryRun) return chip('No hardware check (dry run)', '#7F8C8D', 'Dry run is on, so nothing is asked of the relays');

                // One chip per relay. The headline used to come from the SBC
                // relay alone, which meant a dead hold-open relay hid behind a
                // green "Gate online" — party mode would then fail silently at
                // the only moment it is ever used.
                const NAME = { sbc: 'Gate', holdOpen: 'Hold-open' };
                const devices = (s.devices || []).filter(d => NAME[d.which]);
                if (!devices.length) return chip('Gate status unknown', '#E67E22',
                  'No relay was checked — is the gate relay device ID set below?');

                // Where the gate is sitting, from the Uni's reed switch. Shown
                // first because it is the one thing on this row an admin can
                // see with their own eyes from the courts, and a gate standing
                // open at 9 PM matters more than which relay answered.
                const p = s.position || {};
                // A gate that is not reachable is not in service, so its
                // position goes grey rather than green or amber. The reading
                // itself can still be perfectly current — the Uni is its own
                // device and answers whether or not the relay does — so the
                // label is kept and the tooltip says which part is missing.
                const gateDown = !devices.some(d => d.which === 'sbc' && d.online === true);
                const GREY = '#7F8C8D';
                const offlineNote = ' The gate relay is not reachable, so the gate itself is out of service — '
                  + 'this reading is still live from the Uni.';
                const posChip = !p.deviceId ? null
                  : p.open === true ? chip('GATE OPEN', gateDown ? GREY : '#E67E22',
                    `Reed switch reports the gate standing open · checked ${fmtTs(s.checkedAt)}`
                    + (gateDown ? offlineNote : ''), 'pos')
                  : p.open === false ? chip('Gate closed', gateDown ? GREY : '#27AE60',
                    `Reed switch reports the gate closed · checked ${fmtTs(s.checkedAt)}`
                    + (gateDown ? offlineNote : ''), 'pos')
                  : chip('Gate position unknown', '#7F8C8D',
                    p.reason === 'uni_offline'
                      ? `The Shelly Uni (${p.deviceId}) is off the network, so the gate's position cannot be read.`
                      : `Could not read the reed switch (${p.reason || 'no answer'}). `
                        + 'If the Uni is online, check that the contact is on IN 1 and that the input channel is right.',
                    'pos');

                return [posChip, ...devices.map((d) => {
                  const name = NAME[d.which];
                  const id = d.deviceId || 'no device id';
                  if (d.online === true) return chip(`${name} online`, '#27AE60',
                    `${id} reachable via ${d.api || 'cloud'} · checked ${fmtTs(s.checkedAt)} · click to re-check`, d.which);
                  if (d.online === false) return chip(`${name.toUpperCase()} OFFLINE`, '#C0392B',
                    d.which === 'sbc'
                      ? `Shelly cannot reach ${id} — entries will be refused until it is back. Check power and the CB-Gate wifi.`
                      : `Shelly cannot reach ${id} — entry still works, but party mode will not hold the gate open.`,
                    d.which);
                  // "Could not tell" is not "offline": an expired auth key and a
                  // relay that is off the network answer identically, so the
                  // tooltip names both rather than picking one.
                  return chip(`${name} status unknown`, '#E67E22',
                    `Could not tell (${d.reason || 'no answer'}) — this is not the same as offline. `
                    + `Either ${id} is off the CB-Gate wifi or the Shelly auth key is no longer valid; `
                    + `the function logs record what Shelly actually replied. Click to re-check.`,
                    d.which);
                })];
              })()}
              {/* The keypad, as last heard from. It checks in every 10 minutes,
                  so 25 minutes of silence means it is offline — rather than
                  someone finding out at the driveway. */}
              {(gateStatus?.keypads || []).map(k => {
                const last = k.lastSeenAt ? new Date(k.lastSeenAt) : null;
                const mins = last ? Math.round((Date.now() - last.getTime()) / 60000) : null;
                const ago = mins == null ? 'never'
                  : mins < 1 ? 'just now' : mins < 90 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
                const online = mins != null && mins <= 25;
                const detail = [
                  `Device ${k.deviceId}`,
                  last ? `last heard ${last.toLocaleString()}` : 'never heard from',
                  k.firmware && `firmware ${k.firmware}`,
                  k.rssi != null && `signal ${k.rssi} dBm`,
                  k.uptimeSec != null && `up ${Math.round(k.uptimeSec / 3600)}h`,
                  k.resetReason && `last reboot: ${k.resetReason}`,
                  k.offlineRestarts ? `${k.offlineRestarts} restart(s) for being offline` : null,
                  // Reads the board received and threw away since its last boot.
                  k.droppedReads && (k.droppedReads.parity || k.droppedReads.key || k.droppedReads.odd)
                    ? `reads dropped since boot: ${[
                      k.droppedReads.parity && `${k.droppedReads.parity} card (parity)`,
                      k.droppedReads.key && `${k.droppedReads.key} key`,
                      k.droppedReads.odd && `${k.droppedReads.odd} unreadable (last ${k.droppedReads.oddBits}-bit)`,
                    ].filter(Boolean).join(', ')}`
                    : k.droppedReads && 'no reads dropped since boot',
                  k.offlineListCount != null && `offline PIN list: ${k.offlineListCount} code${k.offlineListCount === 1 ? '' : 's'}${k.offlineListCurrent ? '' : ' (keypad picks up the latest at its next check-in)'}`,
                  k.locked && 'LOCKED after repeated bad codes',
                  !k.lastHeartbeatAt && 'no check-ins yet — older firmware',
                ].filter(Boolean).join(' · ');
                return (
                  <Tooltip key={k.deviceId} title={detail}>
                    <Chip size="small" onClick={loadGateStatus}
                      label={k.locked ? `Keypad locked · ${ago}` : online ? `Keypad online · ${ago}` : `Keypad offline · last heard ${ago}`}
                      sx={{ backgroundColor: k.locked ? '#E67E22' : online ? '#27AE60' : '#C0392B', color: 'white', fontWeight: 700, cursor: 'pointer' }} />
                  </Tooltip>
                );
              })}
              <FormControlLabel
                control={<Switch size="small" checked={config.autoProvisionOnMembership === true}
                  disabled={busy === 'config'}
                  onChange={e => toggleConfigFlag('autoProvisionOnMembership', e.target.checked)} />}
                label={<span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>Auto-provision on membership</span>}
              />
              <FormControlLabel
                control={<Switch size="small" checked={config.dryRun !== true}
                  disabled={busy === 'config' || !config.shelly?.sbcDeviceId}
                  onChange={e => toggleConfigFlag('dryRun', !e.target.checked)} />}
                label={<span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>Arm hardware (dry run off)</span>}
              />
              {/* Turn on only once the ESP32's relay is wired into the LA412.
                  On with nothing wired, keypad entries are approved and
                  logged but nothing opens the gate. */}
              <Tooltip title="On: an approved keypad code is opened by the ESP32's own relay and the server skips the Shelly — faster, and not dependent on Shelly's cloud. Only turn this on once that relay is wired into the LA412; with it unwired, approved codes open nothing.">
                <FormControlLabel
                  control={<Switch size="small" checked={config.keypadOpensLocally === true}
                    disabled={busy === 'config'}
                    onChange={e => toggleConfigFlag('keypadOpensLocally', e.target.checked)} />}
                  label={<span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>Keypad opens the gate itself (ESP32 relay)</span>}
                />
              </Tooltip>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              settings/gateConfig doesn't exist yet. Saving hardware settings below will create it.
            </Alert>
          )}
        </Paper>

        {/* Recorded health. pollGateStatus checks every 5 minutes and records
            only when the state CHANGES, so this is a list of events rather than
            a sampling log — and a quiet month is an empty one. */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ color: GOLD, fontWeight: 700 }}>Gate Status History</Typography>
            <IconButton size="small" onClick={loadStatusLog} sx={{ color: GOLD }}><RefreshIcon /></IconButton>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mb: 1 }}>
            One row whenever the gate’s state changes, or any relay drops off or
            comes back. “Unknown” means the check could not get an answer, which is
            not the same as the gate being down.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>When</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>State</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>Previous lasted</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statusLog.map(r => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.75)', borderColor: 'rgba(255,255,255,0.08)', whiteSpace: 'nowrap' }}>{fmtTs(r.at)}</TableCell>
                    <TableCell sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                      <Chip size="small" label={STATE_LABELS[r.state] || r.state}
                        sx={{ backgroundColor: STATE_COLORS[r.state] || '#7F8C8D', color: 'white', fontWeight: 700 }} />
                      {/* Otherwise a relay-only change reads as a duplicate of
                          the row above: same state, no visible reason. */}
                      {r.trigger === 'devices' && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.45)' }}>
                          relay change
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.08)', whiteSpace: 'nowrap' }}>
                      {r.previousState && r.previousMs
                        ? `${STATE_LABELS[r.previousState] || r.previousState} for ${fmtDur(r.previousMs)}`
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      {r.detail || r.reason || '—'}
                      {Array.isArray(r.devices) && r.devices.length > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace' }}>
                          {r.devices.map(d => `${d.which}:${d.online === true ? 'up' : d.online === false ? 'DOWN' : '?'}`).join('  ')}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {statusLog.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      No status recorded yet — the first row appears within 5 minutes of the poller deploying.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        </>)}

        {tab === 'hardware' && (<>
        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
          {/* Shelly hardware — editable here so the Firestore console isn't
              needed to bring the gate online. */}
          <Box sx={{ mt: config ? 2 : 0, pt: config ? 2 : 0, borderTop: config ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
            <Typography variant="subtitle2" sx={{ color: GOLD, fontWeight: 700, mb: 1 }}>
              Shelly Hardware
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField size="small" label="Cloud server" sx={{ flex: '1 1 260px' }}
                value={hwForm.server}
                onChange={e => { setHwDirty(true); setHwForm(f => ({ ...f, server: e.target.value })); }}
                placeholder="shelly-271-eu.shelly.cloud"
                helperText="Host only — no https://"
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.6)' } }}
                sx={{ flex: '1 1 260px', '& .MuiInputBase-input': { color: 'white' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.45)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' } }} />
              <TextField size="small" label="Gate relay device ID"
                value={hwForm.sbcDeviceId}
                onChange={e => { setHwDirty(true); setHwForm(f => ({ ...f, sbcDeviceId: e.target.value })); }}
                placeholder="e4b0636c2fc8"
                helperText="12 hex chars (MAC, no colons)"
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.6)' } }}
                sx={{ flex: '1 1 200px', '& .MuiInputBase-input': { color: 'white' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.45)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' } }} />
              <TextField size="small" label="Hold-open relay ID (optional)"
                value={hwForm.holdOpenDeviceId}
                onChange={e => { setHwDirty(true); setHwForm(f => ({ ...f, holdOpenDeviceId: e.target.value })); }}
                helperText="Wired to the OPEN input — party mode"
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.6)' } }}
                sx={{ flex: '1 1 200px', '& .MuiInputBase-input': { color: 'white' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.45)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' } }} />
              <TextField size="small" label="Gate sensor Uni ID (optional)"
                value={hwForm.uniDeviceId}
                onChange={e => { setHwDirty(true); setHwForm(f => ({ ...f, uniDeviceId: e.target.value })); }}
                helperText="Shelly Plus Uni — reed switch on IN 1"
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.6)' } }}
                sx={{ flex: '1 1 200px', '& .MuiInputBase-input': { color: 'white' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.45)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' } }} />
              <Button variant="outlined" disabled={hwSaving || !hwDirty} onClick={saveHardware}
                sx={{ mt: 0.5, color: GOLD, borderColor: GOLD, whiteSpace: 'nowrap' }}>
                {hwSaving ? 'Saving…' : 'Save hardware'}
              </Button>
            </Box>
            {hwForm.uniDeviceId ? (
              <FormControlLabel sx={{ mt: 1 }}
                control={<Switch size="small" checked={config?.shelly?.uniInputInverted === true}
                  disabled={busy === 'config'}
                  onChange={e => toggleShellyFlag('uniInputInverted', e.target.checked)} />}
                label={<span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                  Reed switch reads backwards — flip open/closed
                </span>}
              />
            ) : null}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mt: 1 }}>
              Auto-off is set on each device and the two are opposites, because the code only ever
              sends “turn on”. <strong>Gate relay</strong> (SBC input): auto-off ENABLED at ~500&nbsp;ms,
              so each trigger is one button press — without it the relay latches closed.
              <strong> Hold-open relay</strong> (OPEN input): auto-off DISABLED, or the hold releases
              itself after half a second.
            </Typography>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
          {/* Entry limits. Three different questions, and it is easy to reach for
              the wrong one: the lockout is about wrong codes, the cooldown is
              about one person repeating, the cap is about the whole club. */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.12)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Entry limits · 0 switches one off · blank uses the built-in default
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <FormControlLabel
                control={<Switch size="small" checked={limForm.enforce}
                  onChange={e => { setLimDirty(true); setLimForm(f => ({ ...f, enforce: e.target.checked })); }} />}
                label={<span style={{ color: limForm.enforce ? GOLD : 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: limForm.enforce ? 700 : 400 }}>
                  {limForm.enforce ? 'Enforcing — people are turned away' : 'Watching only — nobody is turned away'}
                </span>}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {[
                ['cooldown', 'Same code, again after', 'seconds · default 10', 150],
                ['maxHour', 'Club entries per hour', 'default 240', 140],
                ['failThreshold', 'Lock after N wrong codes', 'default 5', 165],
                ['lockMinutes', 'Lock for', 'minutes, doubling · default 5', 175],
              ].map(([key, label, helper, w]) => (
                <TextField key={key} size="small" label={label} value={limForm[key]} type="number"
                  onChange={e => { setLimDirty(true); setLimForm(f => ({ ...f, [key]: e.target.value })); }}
                  helperText={helper}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ width: w, '& .MuiInputBase-input': { color: 'white' },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
                        '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.4)' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }} />
              ))}
              <Button size="small" variant="contained" disabled={!limDirty || limSaving}
                onClick={saveLimits}
                sx={{ mt: 0.5, backgroundColor: GOLD, color: NAVY, fontWeight: 700 }}>
                {limSaving ? 'Saving…' : 'Save limits'}
              </Button>
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                Limits do not apply to these codes
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {PIN_HOLDER_TYPES.map(t => {
                  const on = limForm.exempt.includes(t);
                  return (
                    <Chip key={t} label={t} size="small" clickable
                      onClick={() => {
                        setLimDirty(true);
                        setLimForm(f => ({ ...f,
                          exempt: on ? f.exempt.filter(x => x !== t) : [...f.exempt, t] }));
                      }}
                      sx={{
                        backgroundColor: on ? 'rgba(201,168,76,0.25)' : 'transparent',
                        color: on ? GOLD : 'rgba(255,255,255,0.5)',
                        border: `1px solid ${on ? GOLD : 'rgba(255,255,255,0.2)'}`,
                        fontWeight: on ? 700 : 400,
                      }} />
                  );
                })}
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mt: 1 }}>
                A parent at drop-off arrives with two cars on one household code, and a renting group
                arrives together — a cooldown on those strands the second car at the street. The limits
                are for a member's own code, which is the one being handed around. Anything not ticked
                here is limited, so a new kind of code is limited by default rather than escaping quietly.
                The master ignores all of it, lockout included.
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mt: 1 }}>
                While watching only, a breach is recorded on the entry it would have refused
                (<code>limitWouldDeny</code> in the log) and the person still gets in. Let real traffic run
                through it before turning anyone away — the numbers above are a guess until they have met
                a Saturday.
              </Typography>
            </Box>
          </Paper>
        </Paper>
        </>)}

        {tab === 'pins' && (<>
        {/* Keypad PINs */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Typography sx={{ color: GOLD, fontWeight: 700, flex: 1 }}>Keypad PINs</Typography>
            <FormControlLabel
              control={<Switch size="small" checked={showRevoked}
                onChange={e => { setShowRevoked(e.target.checked); loadPins(e.target.checked); }} />}
              label={<span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>Show revoked</span>}
            />
            <Button size="small" variant="contained" onClick={openIssueDialog}
              sx={{ backgroundColor: GOLD, color: NAVY, fontWeight: 700 }}>
              Issue a PIN
            </Button>
          </Box>

          {/* A dry run of the real thing: same hash, same lookup, same
              eligibility engine, then it stops. No relay, no useCount, and a
              consumable master is not spent by being checked. */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
            <TextField size="small" label="Check a PIN" value={testPin}
              onChange={e => { setTestPin(e.target.value.replace(/\D/g, '').slice(0, 10)); setTestResult(null); }}
              inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
              sx={{ width: 150, '& .MuiInputBase-root': { color: 'white' },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(201,168,76,0.35)' } }} />
            <Button size="small" variant="outlined" disabled={pinBusy === 'test' || !testPin}
              onClick={checkPin}
              sx={{ color: GOLD, borderColor: 'rgba(201,168,76,0.5)' }}>
              {pinBusy === 'test' ? 'Checking…' : 'Check'}
            </Button>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
              Tells you what the keypad would say. Does not open the gate.
            </Typography>
          </Box>

          {testResult && (
            <Alert severity={testResult.ok ? 'success' : 'warning'} sx={{ mb: 1.5 }}
              onClose={() => setTestResult(null)}>
              {testResult.ok ? (
                <>
                  <strong>Would open.</strong>{' '}
                  {testResult.matched?.label || 'that PIN'}
                  {testResult.matched?.holderType ? ` · ${testResult.matched.holderType}` : ''}
                  {testResult.matched?.windowText ? ` · ${testResult.matched.windowText}` : ''}
                  {testResult.matched?.linkedToAccount === false
                    && ' · not linked to an account, so the log names the label'}
                  {testResult.matched?.consumable
                    && ' · one-off: using it at the gate spends it, checking it here does not'}
                  {/* Said plainly, because a pass here is not a pass at the gate. */}
                  <div style={{ fontSize: '0.8rem', marginTop: 4, opacity: 0.85 }}>
                    The keypad lockout is counted per device and is not checked here, and the
                    relay is hardware either way.
                  </div>
                </>
              ) : (
                <>
                  <strong>Would not open — {testResult.reason}.</strong>
                  {testResult.reason === 'pin_not_recognized'
                    && ' No PIN on file hashes to that code.'}
                  {testResult.reason === 'pin_revoked'
                    && ' That code was withdrawn, and somebody evidently still has it.'}
                  {/* Two separate refusals, and which one it is tells you
                      whether the day or the clock is the problem. */}
                  {(testResult.reason === 'outside_allowed_hours' || testResult.reason === 'outside_allowed_days')
                    && ` Right code, wrong ${testResult.reason === 'outside_allowed_days' ? 'day' : 'time'}`
                      + `${testResult.matched?.windowText ? ` — ${testResult.matched.windowText}` : ''}.`}
                  {testResult.reason === 'access_expired' && ' That code has passed its end date.'}
                  {testResult.reason === 'membership_inactive'
                    && ' A member code, and that membership is not active or trialing — frozen, canceled or lapsed. It works again the moment the membership does.'}
                  {testResult.reason === 'member_not_linked'
                    && ' A member code with no account behind it, so there is no membership to check. Reissue it against the account.'}
                  {testResult.reason === 'gate_disabled'
                    && ' The whole gate is switched off in settings, not just this code.'}
                  {testResult.reason === 'gate_not_configured'
                    && ' settings/gateConfig is missing, so every press is refused before the code is read.'}
                </>
              )}
            </Alert>
          )}


          {/* Master PIN — the code you read out to the plumber. Shown in the
              clear because reading it out is the job; spent on first use, and
              the keypad mints its replacement, so this card is never empty and
              never stale. */}
          <Paper variant="outlined" sx={{
            p: 1.5, mb: 1.5, borderRadius: 2,
            backgroundColor: 'rgba(201,168,76,0.08)', borderColor: 'rgba(201,168,76,0.45)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                  Master PIN · one-off access
                </Typography>
                <Typography sx={{
                  color: GOLD, fontWeight: 800, fontSize: '1.7rem', lineHeight: 1.15,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.18em',
                }}>
                  {masterPin?.pin || (masterBusy ? '······' : '••••••')}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block' }}>
                  {masterPin?.issuedAt
                    ? `issued ${new Date(masterPin.issuedAt).toLocaleString()}`
                    : ''}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              {/* One button, two jobs: it fetches the code the first time and
                  re-fetches it after that. The code is spent on first use, so a
                  number left on screen goes stale silently — which is why this
                  stays one click away rather than being replaced by a reveal
                  that only works once. */}
              <Button size="small" variant="outlined" disabled={masterBusy}
                onClick={() => { setMasterCopied(false); loadMasterPin(); }}
                sx={{ color: GOLD, borderColor: 'rgba(201,168,76,0.5)' }}>
                {masterBusy ? '…' : masterPin?.pin ? 'Refresh' : 'Show PIN'}
              </Button>
              <Button size="small" variant="outlined" disabled={!masterPin?.pin || masterBusy}
                onClick={copyMaster}
                sx={{ color: GOLD, borderColor: 'rgba(201,168,76,0.5)' }}>
                {masterCopied ? 'Copied' : 'Copy'}
              </Button>
              <Button size="small" variant="outlined" disabled={masterBusy} onClick={rotateMaster}
                sx={{ color: GOLD, borderColor: 'rgba(201,168,76,0.5)' }}>
                {masterBusy ? 'Working…' : 'Replace'}
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 1 }}>
              Works any day, any hour. It stops working the moment it is used, and a fresh code takes its
              place — so hit Show PIN when you are about to read it out, because the number here is a
              snapshot from when you last asked and a code used since then is already dead. Showing it is
              itself logged, which is why opening this page no longer does it for you. Replace burns the
              current one immediately, for when it went to the wrong person.
            </Typography>
          </Paper>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            {['parents', 'coaches'].map(aud => (
              <Box key={aud} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Button size="small" variant="outlined" disabled={!!pinBusy}
                  onClick={() => bulkIssue(aud, true)}
                  sx={{ color: GOLD, borderColor: 'rgba(201,168,76,0.5)' }}>
                  {pinBusy === `bulk:${aud}` ? 'Checking…' : `Preview: all ${aud}`}
                </Button>
              </Box>
            ))}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', alignSelf: 'center' }}>
              Preview first — it lists who would get one without issuing or emailing anything.
            </Typography>
          </Box>

          {bulkReport && (
            <Alert severity={bulkReport.dryRun ? 'info' : 'success'} onClose={() => setBulkReport(null)} sx={{ mb: 1.5 }}>
              {bulkReport.dryRun ? (
                <>
                  <strong>{bulkReport.eligible}</strong> {bulkReport.audience} have no live PIN
                  {bulkReport.remaining > 0 && ` (${bulkReport.remaining} beyond the first batch — run it again after)`}.
                  {bulkReport.samples?.length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      {bulkReport.samples.slice(0, 8).map(x => x.name || x.email).join(', ')}
                      {bulkReport.samples.length > 8 ? ' …' : ''}
                    </Typography>
                  )}
                  <Button size="small" sx={{ mt: 1, fontWeight: 700 }}
                    disabled={!!pinBusy || bulkReport.eligible === 0}
                    onClick={() => bulkIssue(bulkReport.audience, false)}>
                    {pinBusy ? 'Issuing…' : `Issue and email ${Math.min(bulkReport.eligible, 120)} PINs`}
                  </Button>
                </>
              ) : (
                <>
                  Issued <strong>{bulkReport.issued}</strong>, emailed <strong>{bulkReport.emailed}</strong>
                  {bulkReport.skippedNoEmail > 0 && `, ${bulkReport.skippedNoEmail} had no email address`}
                  {bulkReport.remaining > 0 && `. ${bulkReport.remaining} still to go — run it again.`}
                  {bulkReport.failed?.length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      Failed: {bulkReport.failed.map(f => f.name).join(', ')}
                    </Typography>
                  )}
                </>
              )}
            </Alert>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['PIN holder', 'Type', 'Window', 'Used', 'Actions'].map((h, i) => (
                    <TableCell key={h} align={i === 4 ? 'right' : 'left'}
                      sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pins.map(p => (
                  <TableRow key={p.id}>
                    <TableCell sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.08)' }}>
                      {p.label}
                      {p.shared && (
                        <Chip label="shared" size="small"
                          sx={{ ml: 0.75, height: 17, fontSize: '0.6rem', backgroundColor: 'rgba(201,168,76,0.25)', color: GOLD }} />
                      )}
                      {p.enabled === false && (
                        <Chip label="revoked" size="small"
                          sx={{ ml: 0.75, height: 17, fontSize: '0.6rem', backgroundColor: '#7F8C8D', color: 'white' }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      {p.holder?.type || '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.08)', fontSize: '0.78rem' }}>
                      {pinWindow(p.rules)}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.08)', fontSize: '0.78rem' }}>
                      {p.useCount || 0}
                      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.4)' }}>
                        {p.lastUsed ? fmtTs(p.lastUsed) : 'never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.08)', whiteSpace: 'nowrap' }}>
                      {pinBusy === p.id ? <CircularProgress size={18} sx={{ color: GOLD }} /> : (
                        <>
                          <Tooltip title="Read this code back — logged as a reveal">
                            <Button size="small" onClick={() => revealPin(p)} sx={{ color: GOLD, fontWeight: 700 }}>Show</Button>
                          </Tooltip>
                          {p.enabled !== false && (
                            <>
                              <Tooltip title="New code now; the old one still works for 12 hours">
                                <Button size="small" onClick={() => rotatePin(p)} sx={{ color: 'white', fontWeight: 700 }}>Rotate</Button>
                              </Tooltip>
                              <Button size="small" onClick={() => revokePin(p)} sx={{ color: '#C0392B', fontWeight: 700 }}>Revoke</Button>
                            </>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {pinsLoaded && pins.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      No PINs yet. Issue one above, or preview a bulk run for parents or coaches.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <GateCardsPanel users={users} displayName={displayName} onMessage={setMsg} />

        <KeypadHistory events={gateStatus?.keypadEvents} />
        </>)}

        {tab === 'access' && (<>
        {/* Provisioning */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <Typography sx={{ color: GOLD, fontWeight: 700, mb: 1 }}>Access Provisioning</Typography>
          <TextField
            fullWidth size="small" placeholder="Search by name or email to grant access…"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ mb: 1.5, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
            {search.trim() ? 'Search results' : 'Users with gate access'} (max 25 shown)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>User</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>Status</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>Last Used</TableCell>
                  <TableCell align="right" sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map(u => {
                  const ga = u.gateAccess;
                  return (
                    <TableRow key={u.id}>
                      <TableCell sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.08)' }}>
                        {displayName(u)}
                        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.45)' }}>{u.email}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                        {ga
                          ? <Chip size="small" label={ga.enabled ? 'Enabled' : 'Disabled'}
                              sx={{ backgroundColor: ga.enabled ? '#27AE60' : '#7F8C8D', color: 'white', fontWeight: 600 }} />
                          : <Chip size="small" label="No access" sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }} />}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.08)' }}>
                        {fmtTs(ga?.lastUsed)}
                      </TableCell>
                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.08)', whiteSpace: 'nowrap' }}>
                        {busy === u.id ? <CircularProgress size={18} sx={{ color: GOLD }} /> : (
                          <>
                            {(!ga || !ga.enabled) && (
                              <Button size="small" onClick={() => provision(u.id, true)}
                                sx={{ color: '#27AE60', fontWeight: 700 }}>Enable</Button>
                            )}
                            {ga?.enabled && (
                              <>
                                <Tooltip title="Regenerate TOTP secret (invalidates the old QR/code)">
                                  <IconButton size="small" onClick={() => provision(u.id, true, true)} sx={{ color: GOLD }}>
                                    <KeyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Run this member's entry as if they scanned — triggers the gate and consumes a day pass">
                                  <Button size="small" onClick={() => testMemberEntry(u.id)}
                                    sx={{ color: GOLD, fontWeight: 700 }}>Test entry</Button>
                                </Tooltip>
                                <Button size="small" onClick={() => provision(u.id, false)}
                                  sx={{ color: '#C0392B', fontWeight: 700 }}>Disable</Button>
                              </>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      {search.trim() ? 'No users match.' : 'No one has gate access yet — search above to enable someone.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Paying members with no Torch account */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ color: GOLD, fontWeight: 700 }}>Paying members with no Torch account</Typography>
            <IconButton size="small" onClick={loadShells} sx={{ color: GOLD }}><RefreshIcon /></IconButton>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 1.5 }}>
            Creates an account for each Stripe membership that matches no Torch login, holding their membership
            and payment history, so they count as members. When they sign up with the email they pay with, it joins
            their new login automatically. If they sign up under a different email, enter it below and Link.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Button size="small" variant="outlined" disabled={!!shellBusy} onClick={() => runShells(true)}
              sx={{ borderColor: GOLD, color: GOLD }}>
              {shellBusy === 'dry' ? 'Checking…' : 'Dry run'}
            </Button>
            <Button size="small" variant="contained" disabled={!!shellBusy}
              onClick={() => runShells(false)}
              sx={{ backgroundColor: GOLD, color: '#0D1E35', fontWeight: 700 }}>
              {shellBusy === 'live' ? 'Creating…' : 'Create accounts'}
            </Button>
          </Box>
          {shellReport && (
            <Box sx={{ maxHeight: 200, overflowY: 'auto', backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 1, p: 1, mb: 1.5 }}>
              {shellReport.created.map(e => (
                <Typography key={e.subscriptionId} variant="caption" sx={{ display: 'block', color: '#82E0AA', fontFamily: 'monospace' }}>
                  {shellReport.dryRun ? 'would create' : 'created'} · {e.firstName} {e.lastName} · {e.email} · {e.tierName} · {e.status} · {e.purchases} payments
                </Typography>
              ))}
              {shellReport.skipped.map(e => (
                <Typography key={e.subscriptionId} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.55)', fontFamily: 'monospace' }}>
                  skipped · {e.name || e.email || e.subscriptionId} · {e.reason}
                </Typography>
              ))}
            </Box>
          )}
          {shells.length > 0 && shells.map(sh => (
            <Box key={sh.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.75, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>{sh.firstName} {sh.lastName}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  {sh.email} · {sh.membership?.tierName} · {sh.membership?.status}
                </Typography>
              </Box>
              <TextField size="small" placeholder="Their Torch login email"
                value={shellLinkEmail[sh.id] || ''}
                onChange={e => setShellLinkEmail(prev => ({ ...prev, [sh.id]: e.target.value }))}
                sx={{ flex: '1 1 200px', '& .MuiInputBase-input': { color: 'white', fontSize: '0.82rem' } }} />
              <Button size="small" variant="outlined" disabled={!!shellBusy || !(shellLinkEmail[sh.id] || '').trim()}
                onClick={() => linkShell(sh)} sx={{ borderColor: GOLD, color: GOLD }}>
                {shellBusy === sh.id ? 'Linking…' : 'Link'}
              </Button>
            </Box>
          ))}
          {shells.length === 0 && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
              No unlinked member accounts. Press refresh after creating them.
            </Typography>
          )}
        </Paper>
        </>)}

        {tab === 'migration' && (<>
        {/* Stripe membership migration (vb-scores -> Torch) — temporary tooling */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <Typography sx={{ color: GOLD, fontWeight: 700, mb: 1 }}>Stripe Membership Migration</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Button variant="outlined" disabled={migBusy} onClick={() => runMigration(true)}
              sx={{ color: GOLD, borderColor: GOLD }}>
              {migBusy ? 'Running…' : 'Dry Run'}
            </Button>
            <Button variant="contained" disabled={migBusy || !migReport || !migReport.dryRun}
              onClick={() => setMigConfirm(true)}
              sx={{ backgroundColor: '#C0392B', color: 'white', fontWeight: 700 }}>
              Run Live Migration
            </Button>
            <Button variant="outlined" disabled={migBusy} onClick={() => runBackfill(true)}
              sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.4)' }}>
              Backfill Purchases (Dry Run)
            </Button>
            <Button variant="outlined" disabled={migBusy} onClick={() => runBackfill(false)}
              sx={{ color: '#E67E22', borderColor: '#E67E22' }}>
              Backfill Purchases (Live)
            </Button>
            <Button variant="outlined" disabled={migBusy} onClick={() => runChargeBackfill(true)}
              sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.4)' }}>
              Backfill Charges (Dry Run)
            </Button>
            <Button variant="outlined" disabled={migBusy || !chargeReport || !chargeReport.dryRun}
              onClick={() => runChargeBackfill(false)}
              sx={{ color: '#E67E22', borderColor: '#E67E22' }}>
              Backfill Charges (Live)
            </Button>
            <Button variant="outlined" disabled={migBusy} onClick={() => runRefundBackfill(true)}
              sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.4)' }}>
              Backfill Refunds (Dry Run)
            </Button>
            <Button variant="outlined" disabled={migBusy || !refundReport || !refundReport.dryRun}
              onClick={() => runRefundBackfill(false)}
              sx={{ color: '#E67E22', borderColor: '#E67E22' }}>
              Backfill Refunds (Live)
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
            Live run unlocks only after a dry run in this session. Live stamps subscription metadata in Stripe and writes membership objects to user docs.
          </Typography>
          {backfillReport && (
            <Box sx={{ mt: 1, mb: 1.5, p: 1.5, borderRadius: 1, backgroundColor: 'rgba(41,128,185,0.12)', border: '1px solid rgba(41,128,185,0.4)' }}>
              <Typography variant="body2" sx={{ color: '#85C1E9', fontWeight: 700 }}>
                Purchase backfill {backfillReport.dryRun ? 'DRY RUN' : 'LIVE'} · {backfillReport.written} records · ${backfillReport.totalDollars}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}>
                {backfillReport.unlinkedSubs} of {backfillReport.subs} subscriptions have no Torch account —
                {' '}{backfillReport.unlinkedWritten} records (${backfillReport.unlinkedDollars}) would be written with
                {' '}<code>userId: null</code> and the Stripe email, so the income lands in Reports now and the
                {' '}migration can claim them later.
                {backfillReport.tierUnmapped > 0 && ` ${backfillReport.tierUnmapped} have a price that maps to no membership tier — recorded as "Membership".`}
              </Typography>
              {backfillReport.skippedNonMembership?.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ color: '#F0B27A', display: 'block', mt: 0.5, fontWeight: 700 }}>
                    Skipped {backfillReport.skippedNonMembership.length} subscription(s) — not recognizable as membership:
                  </Typography>
                  <Box sx={{ maxHeight: 120, overflowY: 'auto', backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 1, p: 1, mt: 0.5 }}>
                    {backfillReport.skippedNonMembership.map((s, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                        {s.email || '(no email)'} · {s.subscriptionId} · {s.status} · {s.product} · {s.priceId || 'no price'}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
              {backfillReport.noPaidInvoices?.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ color: '#F1948A', display: 'block', mt: 0.75, fontWeight: 700 }}>
                    {backfillReport.noPaidInvoices.length} subscription(s) have NO paid invoices in Stripe —
                    these members will show an empty purchase history:
                  </Typography>
                  <Box sx={{ maxHeight: 160, overflowY: 'auto', backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 1, p: 1, mt: 0.5 }}>
                    {backfillReport.noPaidInvoices.map((n, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                        {n.email || '(no email)'} · {n.subscriptionId} · {n.status} · {n.tierName || '(no tier)'} · {n.linked ? 'linked' : 'UNLINKED'}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
              {backfillReport.skippedAthletePlan > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mt: 0.5 }}>
                  {backfillReport.skippedAthletePlan} junior session plan subscription(s) skipped — handled by the athlete purchase flow.
                </Typography>
              )}
              {backfillReport.perSub?.filter(p => p.linked === false).length > 0 && (
                <Box sx={{ maxHeight: 160, overflowY: 'auto', backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 1, p: 1, mt: 0.75 }}>
                  {backfillReport.perSub.filter(p => p.linked === false).map((p, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.75)', fontFamily: 'monospace' }}>
                      {p.email || '(no email)'} · {p.subscriptionId} · {p.tierName} · {p.records} inv · ${p.dollars}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
          {chargeReport && (
            <Box sx={{ mt: 1, mb: 1.5, p: 1.5, borderRadius: 1, backgroundColor: 'rgba(39,174,96,0.12)', border: '1px solid rgba(39,174,96,0.4)' }}>
              <Typography variant="body2" sx={{ color: '#82E0AA', fontWeight: 700 }}>
                Standalone charges {chargeReport.dryRun ? 'DRY RUN' : 'LIVE'} · {chargeReport.written} records · ${chargeReport.totalDollars}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}>
                {chargeReport.chargesSeen} charges scanned ·{' '}
                {chargeReport.coveredByInvoice} already on a subscription invoice,{' '}
                {chargeReport.coveredBySession} already recorded from checkout,{' '}
                {chargeReport.coveredById} written by an earlier run,{' '}
                {chargeReport.coveredByFingerprint} matched an existing record by payer + amount + date.
                {chargeReport.rejectedByAmount > 0 && ` ${chargeReport.rejectedByAmount} withheld because the amount did not match the payer's tier price.`}
                {' '}Charges on an invoice are keyed by invoice id, the same id the purchase backfill uses,
                so the two tools cannot record one payment twice.
              </Typography>
              {chargeReport.matched?.length > 0 && (
                <Box sx={{ maxHeight: 160, overflowY: 'auto', backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 1, p: 1, mt: 0.75 }}>
                  {chargeReport.matched.map((m, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.8)', fontFamily: 'monospace' }}>
                      {m.created} · {m.email || '(no email)'} · ${m.amount} · {m.tierName} · {m.userId ? 'linked' : 'UNLINKED'}
                    </Typography>
                  ))}
                </Box>
              )}
              {chargeReport.unclassified?.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ color: '#F0B27A', display: 'block', mt: 0.75, fontWeight: 700 }}>
                    {chargeReport.unclassified.length} charge(s) (${chargeReport.unclassifiedDollars}) could not be
                    tied to a member or a tier — NOT written, so nothing lands in the wrong income bucket:
                  </Typography>
                  <Box sx={{ maxHeight: 160, overflowY: 'auto', backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 1, p: 1, mt: 0.5 }}>
                    {chargeReport.unclassified.map((m, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                        {m.created} · {m.email || '(no email)'} · ${m.amount} · {m.description || '(no description)'} · {m.chargeId}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
          {refundReport && (
            <Box sx={{ mt: 1, mb: 1.5, p: 1.5, borderRadius: 1, backgroundColor: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.4)' }}>
              <Typography variant="body2" sx={{ color: '#F1948A', fontWeight: 700 }}>
                Refunds {refundReport.dryRun ? 'DRY RUN' : 'LIVE'} · {refundReport.written} purchases · ${refundReport.refundedDollars}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}>
                {refundReport.refundsSeen} refunds across {refundReport.charges} charges ·{' '}
                {refundReport.alreadyRecorded} already recorded · {refundReport.leagueRegistrations} league registrations
                {' '}(the webhook handles those) · {refundReport.unmatched.length} with no purchase record.
              </Typography>
              {refundReport.matched.map(m => (
                <Typography key={m.chargeId} variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', fontFamily: 'monospace' }}>
                  {m.created} · {m.email || m.userId || '—'} · {m.tierName || '—'} · ${m.refunded} of ${m.amountPaid} · {m.was} → {m.becomes}
                </Typography>
              ))}
              {refundReport.unmatched.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ color: '#F0B27A', display: 'block', mt: 0.5, fontWeight: 700 }}>
                    Refunded in Stripe, no purchase record found:
                  </Typography>
                  {refundReport.unmatched.map(u => (
                    <Typography key={u.chargeId} variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontFamily: 'monospace' }}>
                      {u.created} · {u.email || '—'} · ${u.refunded} · {u.chargeId}
                    </Typography>
                  ))}
                </>
              )}
            </Box>
          )}
          {migReport?.unrecordedRevenue?.paidDollars > 0 && (
            <Box sx={{ mt: 1, mb: 1.5, p: 1.5, borderRadius: 1, backgroundColor: 'rgba(230,126,34,0.12)', border: '1px solid rgba(230,126,34,0.4)' }}>
              <Typography variant="body2" sx={{ color: '#F0B27A', fontWeight: 700 }}>
                ${migReport.unrecordedRevenue.paidDollars.toFixed(2)} of membership revenue is NOT recorded in Torch
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
                {migReport.unrecordedRevenue.paidInvoices} paid invoices across {migReport.unrecordedRevenue.subscriptions} unlinked
                subscription{migReport.unrecordedRevenue.subscriptions === 1 ? '' : 's'}. No purchase docs exist for these, so the
                Reports P&amp;L income excludes them. Linking the subscriptions and re-running the purchase backfill pulls them in.
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                {Object.entries(migReport.unrecordedRevenue.byYear).sort()
                  .map(([y, amt]) => `${y}: $${amt.toFixed(2)}`).join('  ·  ')}
              </Typography>
            </Box>
          )}
          {migReport && (
            <Box sx={{ mt: 1 }}>
              {[['migrated', migReport.dryRun ? 'Would migrate' : 'Migrated', '#27AE60'],
                ['alreadyLinked', 'Already linked', '#7F8C8D'],
                ['unmatchedEmail', 'Unmatched email (needs Torch account)', '#E67E22'],
                ['unmatchedTier', 'Unmatched tier (price not mapped)', '#C0392B'],
                ['conflicts', 'Conflicts (different sub on user)', '#C0392B']]
                .filter(([key]) => (migReport[key] || []).length > 0)
                .map(([key, label, color]) => (
                <Box key={key} sx={{ mb: 1.5 }}>
                  <Chip size="small" label={`${label}: ${migReport[key].length}`}
                    sx={{ backgroundColor: color, color: 'white', fontWeight: 700, mb: 0.5 }} />
                  <Box sx={{ maxHeight: 180, overflowY: 'auto', backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 1, p: 1 }}>
                    {migReport[key].map((e, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.75)', fontFamily: 'monospace' }}>
                        {e.email || '(no email)'} · {e.subscriptionId} · {e.status}{e.tierName ? ` · ${e.tierName}` : ''}{e.priceId ? ` · ${e.priceId}` : ''}{e.existingSubscriptionId ? ` · existing: ${e.existingSubscriptionId}` : ''}
                        {e.paidDollars > 0 && ` · $${e.paidDollars.toFixed(2)} unrecorded (${e.paidInvoices} inv, ${e.firstPaidAt}→${e.lastPaidAt})`}
                        {e.recordedDollars > 0 && ` · $${e.recordedDollars.toFixed(2)} already in the books (${e.recordedInvoices} inv)`}
                        {e.invoiceError && ` · invoice lookup failed: ${e.invoiceError}`}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
              {['migrated','alreadyLinked','unmatchedEmail','unmatchedTier','conflicts'].every(k => (migReport[k] || []).length === 0) && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Report is empty — no matching subscriptions found.</Typography>
              )}
            </Box>
          )}
        </Paper>
        </>)}

        {tab === 'log' && (<>
        {/* Audit log */}
        <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ color: GOLD, fontWeight: 700 }}>Access Log (last 50)</Typography>
            <IconButton size="small" onClick={loadLogs} sx={{ color: GOLD }}><RefreshIcon /></IconButton>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>When</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>Who</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>Result</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>Source</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(l => (
                  <TableRow key={l.id}>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.75)', borderColor: 'rgba(255,255,255,0.08)', whiteSpace: 'nowrap' }}>{fmtTs(l.occurredAt || l.timestamp)}</TableCell>
                    <TableCell sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.08)' }}>{l.userName || l.userId || '—'}</TableCell>
                    <TableCell sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                      <Chip size="small" label={resultLabel(l, cardKinds)}
                        sx={{ backgroundColor: RESULT_COLORS[l.result] || '#7F8C8D', color: 'white', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      {/* Decided by the keypad with no connection and uploaded
                          later, so listed when the server heard but dated when
                          it happened. */}
                      {l.type === 'offline_entry' ? 'keypad (offline)' : l.source || '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      {l.denialReason || l.reason || (l.shellyTriggered ? `→ ${l.shellyTriggered}` : l.opener === 'keypad_relay' ? '→ keypad relay' : '—')}
                      {l.cardHint && <span style={{ fontFamily: 'monospace' }}>{` · card ${l.cardHint}`}</span>}
                      {/* The cloud's own response — the only way to tell an
                          accepted-but-ignored command from a real actuation. */}
                      {l.shellyResponse && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {String(l.shellyResponse).slice(0, 300)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      No gate activity yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        </>)}


        {/* Live migration confirmation */}
        <Dialog open={migConfirm} onClose={() => setMigConfirm(false)} fullWidth maxWidth="xs">
          <DialogTitle>Run LIVE migration?</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 1 }}>
              This writes to Stripe (subscription metadata) and Firestore (membership objects, member roles) for
              {' '}{migReport?.migrated?.length ?? 0} subscriptions. It is not easily reversible.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMigConfirm(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => runMigration(false)}
              sx={{ backgroundColor: '#C0392B', color: 'white', fontWeight: 700 }}>
              Yes, migrate {migReport?.migrated?.length ?? 0} members
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manual open dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs">
          <DialogTitle>Open Gate</DialogTitle>
          <DialogContent>
            {config?.dryRun && <Alert severity="info" sx={{ mb: 2 }}>Dry run is on — this logs the trigger without calling hardware.</Alert>}
            <TextField
              autoFocus fullWidth label="Reason (required, goes in the audit log)"
              value={openReason} onChange={e => setOpenReason(e.target.value)}
              placeholder="e.g. Delivery driver, vendor visit"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" disabled={!openReason.trim() || busy === 'manual'} onClick={manualOpen}
              sx={{ backgroundColor: GOLD, color: NAVY, fontWeight: 700 }}>
              {busy === 'manual' ? 'Triggering…' : 'Open Gate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Issue a PIN */}
        <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Issue a Gate PIN</DialogTitle>
          <DialogContent>
            {issueForm && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField select size="small" label="Who is this for" value={issueForm.holderType}
                  onChange={e => setIssueForm(f => ({ ...f, holderType: e.target.value }))}
                  SelectProps={{ native: true }}>
                  {['parent', 'coach', 'renter', 'event', 'member', 'other'].map(t => <option key={t} value={t}>{t}</option>)}
                </TextField>

                <Box>
                  <TextField fullWidth size="small" label="Search for an account (optional)"
                    value={issueForm.userSearch}
                    onChange={e => setIssueForm(f => ({ ...f, userSearch: e.target.value, holderId: '', holderName: '' }))}
                    placeholder="Name or email" />
                  {issueForm.holderId ? (
                    <Typography variant="caption" sx={{ color: '#27AE60', fontWeight: 700 }}>
                      Attached to {issueForm.holderName}
                    </Typography>
                  ) : issueForm.userSearch.trim().length >= 2 && (
                    <Box sx={{ mt: 0.5, maxHeight: 150, overflowY: 'auto' }}>
                      {users
                        .filter(u => `${displayName(u)} ${u.email || ''}`.toLowerCase().includes(issueForm.userSearch.trim().toLowerCase()))
                        .slice(0, 6)
                        .map(u => (
                          <Button key={u.id} size="small" fullWidth
                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                            onClick={() => setIssueForm(f => ({
                              ...f, holderId: u.id, holderName: displayName(u),
                              label: f.label || displayName(u), userSearch: displayName(u),
                            }))}>
                            {displayName(u)} — {u.email}
                          </Button>
                        ))}
                    </Box>
                  )}
                </Box>

                <TextField size="small" label="Label (what the log will show)"
                  value={issueForm.label}
                  onChange={e => setIssueForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Rodriguez family, Sand delivery" />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                    <Chip key={d} label={d} size="small"
                      color={issueForm.days.includes(i) ? 'primary' : 'default'}
                      variant={issueForm.days.includes(i) ? 'filled' : 'outlined'}
                      onClick={() => setIssueForm(f => ({
                        ...f, days: f.days.includes(i) ? f.days.filter(x => x !== i) : [...f.days, i].sort(),
                      }))} />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ mt: -1.5, color: '#888' }}>
                  No days selected means any day.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField size="small" type="time" label="From" InputLabelProps={{ shrink: true }}
                    value={issueForm.hoursStart}
                    onChange={e => setIssueForm(f => ({ ...f, hoursStart: e.target.value }))} />
                  <TextField size="small" type="time" label="Until" InputLabelProps={{ shrink: true }}
                    value={issueForm.hoursEnd}
                    onChange={e => setIssueForm(f => ({ ...f, hoursEnd: e.target.value }))} />
                  <TextField size="small" type="date" label="Expires" InputLabelProps={{ shrink: true }}
                    value={issueForm.expiresAt}
                    onChange={e => setIssueForm(f => ({ ...f, expiresAt: e.target.value }))} />
                </Box>
                <Typography variant="caption" sx={{ mt: -1.5, color: '#888' }}>
                  Leave the times blank for no hour limit. An evening window may cross midnight — 21:00 until 02:00 works.
                </Typography>

                <TextField size="small" label="Notes" value={issueForm.notes}
                  onChange={e => setIssueForm(f => ({ ...f, notes: e.target.value }))} />

                <FormControlLabel
                  control={<Switch size="small" checked={issueForm.email}
                    onChange={e => setIssueForm(f => ({ ...f, email: e.target.checked }))} />}
                  label={<span style={{ fontSize: '0.85rem' }}>Email the code to them</span>} />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIssueOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={pinBusy === 'issue'} onClick={issuePin}
              sx={{ backgroundColor: GOLD, color: NAVY, fontWeight: 700 }}>
              {pinBusy === 'issue' ? 'Issuing…' : 'Issue PIN'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* The code itself — the only place it is ever shown. */}
        <Dialog open={!!pinReveal} onClose={() => setPinReveal(null)} fullWidth maxWidth="xs">
          <DialogTitle>{pinReveal?.label || 'Gate PIN'}</DialogTitle>
          <DialogContent>
            <Box sx={{
              textAlign: 'center', py: 3, my: 1,
              border: `2px solid ${GOLD}`, borderRadius: 2, backgroundColor: 'rgba(201,168,76,0.08)',
            }}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '2.4rem', fontWeight: 700, letterSpacing: '0.4rem', color: NAVY }}>
                {pinReveal?.pin}
              </Typography>
            </Box>
            {pinReveal?.emailed === true && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Emailed to {pinReveal.sentTo || 'them'}.
              </Alert>
            )}
            {pinReveal?.emailed === false && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Not emailed{pinReveal.emailNote ? ` — ${pinReveal.emailNote}` : ''} Write it down or read it out.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPinReveal(null)}>Done</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
    </Layout>
  );
}

export default AdminGate;
